import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
	makeStyles,
	Typography,
	Grid,
	Button,
	Box
} from '@material-ui/core';

import LabeledSwitch from "./LabeledSwitch";
import WeekToggleButtons from "./WeekToggleButtons";
import TimeSelect from "./TimeSelect";
import AuthorizedUser from "../utils/AuthorizedUser";
import AuthorizedUserContext from "../utils/AuthorizedUserContext";

const styles = makeStyles(theme => ({
	heading: {
		fontWeight: 600
	},

	paddedRow: {
		paddingLeft: "3%",
		paddingRight: "3%",
	},

	rowWidth: {
		width: "94%",
	},

	timeIcon: {
		paddingRight: theme.spacing(.5)
	},

	timeOfDayItem: {
		paddingLeft: theme.spacing(1.5)
	},

	timeOfDay: {
		marginTop: theme.spacing(1.875),

		"& > label": {
			marginTop: theme.spacing(-0.5)
		},

		"& button": {
			marginTop: theme.spacing(0.625)
		}
	},

	sendBlankToggle: {
		justifyContent: "flex-start",
		width: "100%"
	},

	reportHeading: {
		display: "block",
		// marginTop: theme.spacing(4),

		"& > span:last-child": {
			fontWeight: 600,
			paddingRight: theme.spacing(1)
		}
	},
	subHeading: {
		color: theme.palette.secondary.grey
	},
	sourceContainer: {
		paddingLeft: theme.spacing(3),
		paddingRight: theme.spacing(4)
	},
}));

function EmailDigestSettings(props) {
	let classes = styles(),
		paddedRowCls = `${classes.paddedRow} ${classes.rowWidth}`,
		{ notificationConfig, formChangeHandler, digestButtonToggled, settings, gotoProfile } = props;

	const authenticatedUser = useContext(AuthorizedUserContext);

	return (
		<React.Fragment>

			{AuthorizedUser.isAdmin(authenticatedUser) ? (
				<React.Fragment>
					<Grid container direction="row" alignItems="center" spacing={2}>
						<Grid item>
							<FontAwesomeIcon className={classes.iconHeading} icon={["fas", "envelope"]} />
						</Grid>
						<Grid item>
							<Typography className={classes.heading}>Escalation Notifications</Typography>
							<Typography className={classes.subHeading}>You will receive an Email when a team member escalates an alert.</Typography>
						</Grid>
					</Grid>
					<Grid
						container
						wrap="nowrap"
						alignItems="center"
						className={classes.sourceContainer}
						style={{ marginBottom: '20px' }}>
						<React.Fragment>
							<Grid item >
								<LabeledSwitch
									id="email_escalation"
									name="email_escalation"
									className={classes.labeledSwitch}
									checked={notificationConfig.email_escalation}
									onChange={formChangeHandler}
									aria-label="social media alert via text message"
								/>
							</Grid>
							<Grid item>
								<Typography>Email</Typography>
							</Grid>
						</React.Fragment>
					</Grid>

				</React.Fragment>
			) : null}

			{settings && Object.keys(settings.times).length ? (
				<React.Fragment>
					<Grid container direction="row" alignItems="center" spacing={2}>
						<Grid item>
							<FontAwesomeIcon className={classes.iconHeading} icon={["fas", "envelope"]} />
						</Grid>
						<Grid item>
							<Typography className={classes.heading}>Daily Summary of Alerts</Typography>
							<Typography>Set a delivery schedule for your alerts. If a schedule is not set you will receive alert notifications 24 hours, 7 days a week.</Typography>
						</Grid>
					</Grid>
					<Grid container
						wrap="nowrap"
						alignItems="center"
						className={classes.sourceContainer}
						style={{ marginBottom: '20px' }}>
						<Grid item >
							<LabeledSwitch
								id="daily_summary_send_blank"
								name="daily_summary_send_blank"
								label="Receive an email even if there are no new alerts"
								className={classes.labeledSwitch}
								checked={notificationConfig.daily_summary_send_blank}
								onChange={formChangeHandler} />
						</Grid>
					</Grid>

					<Grid item >
						<Typography className={`${classes.heading} ${paddedRowCls}`}>Time of Day</Typography>
						<TimeSelect
							controlId="digestFormControl"
							className={`${paddedRowCls} ${classes.heading}`}
							ariaId="digest-helper-text"
							ariaText="Select the time of day you'd like to receive your summary of alerts"
							selectName="timeOfDaySelect"
							id="timeOfDay"
							name="daily_summary_time"
							value={notificationConfig.daily_summary_time}
							onChange={formChangeHandler}
							settings={settings} />
					</Grid>
					<Grid item >
						<WeekToggleButtons
							id="weekTogglers"
							style={{marginTop: "10px", marginBottom: "10px"}}
							className={`${paddedRowCls} ${classes.timeOfDay} ${classes.heading}`}
							buttonToggled={digestButtonToggled}
							values={[
								notificationConfig.monday_summary,
								notificationConfig.tuesday_summary,
								notificationConfig.wednesday_summary,
								notificationConfig.thursday_summary,
								notificationConfig.friday_summary,
								notificationConfig.saturday_summary,
								notificationConfig.sunday_summary,
							]} />
					</Grid>

					<Grid container wrap="nowrap"
						alignItems="center"
						className={classes.sourceContainer}
						style={{ marginBottom: '20px' }}>
						<Button
							color="primary"
							className={classes.timeButton}
							onClick={gotoProfile}>
							Timezone Settings ({settings.user.timezone})
							<FontAwesomeIcon className={classes.timeIcon} icon={["far", "clock"]} title="Timezone Settings" style={{ marginLeft: 10 }} />
						</Button>
					</Grid>

					<Grid container alignItems="center" spacing={2}>
						<Grid item>
							<FontAwesomeIcon className={classes.iconHeading} icon={["fas", "envelope"]} />
						</Grid>
						<Grid item >
							<Box display="flex" alignItems="center">
								<Typography className={classes.heading}>Weekly Summary Report</Typography>
								<LabeledSwitch
								id="weekly_summary"
								name="weekly_summary"
								checked={notificationConfig.weekly_summary}
								onChange={formChangeHandler}
								className={`${classes.subHeading}`}
								label={notificationConfig.weekly_summary ? "Active" : "Inactive"}
								
							/>
							</Box>
							<Typography>
								Receive an email summary of the system performance, delivered Monday mornings.
							</Typography>
						</Grid>
					</Grid>

					<Grid container alignItems="center" spacing={2}>
						<Grid item>
							<FontAwesomeIcon className={classes.iconHeading} icon={["fas", "envelope"]} />
						</Grid>
						<Grid item>
							<Box display="flex" alignItems="center">
								<Typography className={classes.heading}>Monthly Summary Report</Typography>
								<LabeledSwitch
									id="monthly_summary"
									name="monthly_summary"
									label={notificationConfig.monthly_summary ? "Active" : "Inactive"}
									checked={notificationConfig.monthly_summary}
									onChange={formChangeHandler}
									className={`${classes.subHeading}`} />
							</Box>
							<Typography>Receive an email summary of the system performance, delivered <i>1st of every month</i>.</Typography>
						</Grid>
					</Grid>
				</React.Fragment>
			) : null}
		</React.Fragment>
	);
}

EmailDigestSettings.propTypes = {
	settings: PropTypes.object,
	notificationConfig: PropTypes.object,
	formChangeHandler: PropTypes.func.isRequired,
	digestButtonToggled: PropTypes.func.isRequired,
	gotoProfile: PropTypes.func.isRequired
};

export default EmailDigestSettings;